import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If we're in the middle of an SSO bootstrap, defer the /auth/me probe
    // entirely. Otherwise checkAuth races with /api/sso/exchange and a
    // failing /auth/me can clobber the just-authenticated user.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('sso_token') || window.location.pathname === '/sso/accept') {
        setLoading(false);
        return;
      }
    } catch { /* ignore */ }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      // Sync localStorage
      localStorage.setItem('user', JSON.stringify(data));
    } catch {
      // Auth failed - clear stale data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
